import { useEffect, useLayoutEffect, useRef } from 'react';

import { useTelegram } from './telegram-context';

/** Props of {@link BackButton}. */
export interface BackButtonProps {
  /** Called on each tap of Telegram's back arrow; usually navigates to the previous screen. */
  readonly onBack: () => void;
}

/**
 * Bridge to Telegram's native back arrow (C3 R15, §5.3; technical spec §4). Renders nothing in
 * the page: it shows the arrow on mount, delivers taps to the latest `onBack`, and hides it and
 * unsubscribes on unmount. Proposal, to be validated (port shape).
 *
 * @example <BackButton onBack={() => navigate(-1)} />
 */
export function BackButton({ onBack }: BackButtonProps): null {
  const telegram = useTelegram();
  const latest = useRef(onBack);
  // Layout effect: a tap right after a commit calls this render's onBack.
  useLayoutEffect(() => {
    latest.current = onBack;
  });

  useEffect(() => {
    telegram.showBackButton();
    const unsubscribe = telegram.onBackButtonClick(() => {
      latest.current();
    });
    return () => {
      unsubscribe();
      telegram.hideBackButton();
    };
  }, [telegram]);

  return null;
}
